export interface DutchAddressLookupResult {
  street: string;
  houseNumber: string;
  houseAddition: string;
  address: string;
  zipCode: string;
  city: string;
}

interface PdokAddress {
  postcode?: string;
  huisnummer?: number | string;
  huisletter?: string;
  huisnummertoevoeging?: string;
  toevoeging?: string;
  straatnaam?: string;
  openbare_ruimte_naam?: string;
  woonplaatsnaam?: string;
  woonplaats_naam?: string;
}

export const isValidDutchPostalCode = (postalCode: string) => /^\d{4}\s?[A-Za-z]{2}$/.test(postalCode.trim());
export const isValidDutchHouseNumber = (houseNumber: string) => /^\d{1,5}$/.test(houseNumber.trim());

export const lookupDutchAddress = async (
  zipCode: string,
  houseNumber: string,
  houseAddition: string,
  signal?: AbortSignal,
): Promise<DutchAddressLookupResult | null> => {
  const postal = zipCode.replace(/\s+/g, "").toUpperCase();
  const house = houseNumber.trim();
  const addition = houseAddition.trim();
  const params = new URLSearchParams({ q: [postal, house, addition].filter(Boolean).join(" "), fq: "type:adres", rows: "5", wt: "json" });
  const requestOptions: RequestInit = { headers: { Accept: "application/json" } };
  if (signal) requestOptions.signal = signal;
  const response = await fetch(`https://api.pdok.nl/bzk/locatieserver/search/v3_1/free?${params.toString()}`, requestOptions);
  if (!response.ok) throw new Error("technical");
  const json = await response.json();
  const docs: PdokAddress[] = json?.response?.docs || [];
  const doc = docs.find((item) => {
    const itemPostal = String(item.postcode || "").replace(/\s+/g, "").toUpperCase();
    const itemAddition = String(item.huisletter || item.huisnummertoevoeging || item.toevoeging || "").trim().toUpperCase();
    return itemPostal === postal && String(item.huisnummer || "") === house && (!addition || itemAddition === addition.toUpperCase());
  }) || docs[0];
  if (!doc) return null;
  const street = doc.straatnaam || doc.openbare_ruimte_naam || "";
  const resolvedHouse = String(doc.huisnummer || house);
  const resolvedAddition = doc.huisletter || doc.huisnummertoevoeging || doc.toevoeging || addition;
  return {
    street,
    houseNumber: resolvedHouse,
    houseAddition: resolvedAddition,
    address: [street, `${resolvedHouse}${resolvedAddition || ""}`].filter(Boolean).join(" "),
    zipCode: doc.postcode || postal,
    city: doc.woonplaatsnaam || doc.woonplaats_naam || "",
  };
};

const comparableAddressPart = (value: string | null | undefined) => String(value ?? "").replace(/\s+/g, "").toLowerCase();

/**
 * Address fields as compared for unsaved changes: the formatting the address lookup applies when a
 * form opens (spacing, casing of postcode, street and city) does not count as a change.
 */
export const withComparableAddress = <T extends { address?: string | null; zipCode?: string | null; city?: string | null }>(
  values: T,
): T => ({
  ...values,
  address: comparableAddressPart(values.address),
  zipCode: comparableAddressPart(values.zipCode),
  city: comparableAddressPart(values.city),
});
